import type { StateCreator, StoreApi } from 'zustand';

export interface UndoSlice {
  undo: () => void;
  canUndo: boolean;
  _pauseUndo: () => void;
  _resumeUndo: () => void;
  _saveCheckpoint: () => void;
  _clearHistory: () => void;
}

const MAX_HISTORY = 50;
const BATCH_WINDOW_MS = 500;

/**
 * Zustand middleware that adds undo capability to a store.
 *
 * T = the full store type WITHOUT UndoSlice (e.g. MemeState & MemeActions)
 * trackedKeys = which state keys to snapshot for undo
 *
 * The inner creator returns T, and `get()` provides T & UndoSlice
 * so actions can call `_saveCheckpoint`, `_pauseUndo`, etc.
 */
export function undoMiddleware<T extends object>(
  trackedKeys: (keyof T)[],
  creator: (
    set: StoreApi<T & UndoSlice>['setState'],
    get: StoreApi<T & UndoSlice>['getState'],
    api: StoreApi<T & UndoSlice>
  ) => T,
  resetOnUndo?: Partial<T>
): StateCreator<T & UndoSlice> {
  return (originalSet, get, api) => {
    const history: Partial<T>[] = [];
    let paused = false;
    let lastSnapshotTime = 0;

    function snapshot(): Partial<T> {
      const state = get() as T;
      const snap: Partial<T> = {};
      for (const key of trackedKeys) {
        const val = state[key];
        // Deep clone arrays of elements (shallow spread per element + position spread)
        if (Array.isArray(val)) {
          snap[key] = val.map((item: unknown) => {
            if (item && typeof item === 'object') {
              const el = { ...(item as Record<string, unknown>) };
              if (el.position && typeof el.position === 'object') {
                el.position = { ...(el.position as Record<string, unknown>) };
              }
              if (el.backgroundImageOffset && typeof el.backgroundImageOffset === 'object') {
                el.backgroundImageOffset = { ...(el.backgroundImageOffset as Record<string, unknown>) };
              }
              return el;
            }
            return item;
          }) as T[keyof T];
        } else if (val && typeof val === 'object' && !Array.isArray(val)) {
          // Shallow clone plain objects (viewport, preset, offset)
          snap[key] = { ...val } as T[keyof T];
        } else {
          snap[key] = val;
        }
      }
      return snap;
    }

    function snapshotsEqual(a: Partial<T>, b: Partial<T>): boolean {
      return JSON.stringify(a) === JSON.stringify(b);
    }

    function pushSnapshot(force = false) {
      const now = Date.now();
      // Batch rapid-fire changes (sliders, etc.) into a single undo step
      if (!force && now - lastSnapshotTime < BATCH_WINDOW_MS) {
        return;
      }
      const snap = snapshot();
      if (history.length > 0 && snapshotsEqual(history[history.length - 1], snap)) {
        return;
      }
      history.push(snap);
      lastSnapshotTime = now;
      if (history.length > MAX_HISTORY) {
        history.shift();
      }
    }

    const wrappedSet: StoreApi<T & UndoSlice>['setState'] = (partial, replace) => {
      if (!paused) {
        pushSnapshot();
      }
      originalSet(partial, replace as boolean);
      originalSet({ canUndo: history.length > 0 } as Partial<T & UndoSlice>);
    };

    const originalApiSetState = api.setState;
    api.setState = (partial, replace) => {
      if (!paused) {
        pushSnapshot();
      }
      originalApiSetState(partial, replace);
      originalApiSetState({ canUndo: history.length > 0 } as Partial<T & UndoSlice>);
    };

    const undoActions: UndoSlice = {
      canUndo: false,

      undo: () => {
        if (history.length === 0) return;
        const prev = history.pop()!;
        const wasPaused = paused;
        paused = true;
        originalSet({ ...prev, ...resetOnUndo, canUndo: history.length > 0 } as Partial<T & UndoSlice>);
        paused = wasPaused;
      },

      _pauseUndo: () => {
        paused = true;
      },

      _resumeUndo: () => {
        paused = false;
      },

      _saveCheckpoint: () => {
        pushSnapshot(true);
        originalSet({ canUndo: history.length > 0 } as Partial<T & UndoSlice>);
      },

      _clearHistory: () => {
        history.length = 0;
        originalSet({ canUndo: false } as Partial<T & UndoSlice>);
      },
    };

    const state = creator(wrappedSet, get, api);

    return {
      ...state,
      ...undoActions,
    };
  };
}
